import React, { useState } from 'react'
import { Download } from 'lucide-react'
import { getDocumentIcon, getDocumentColor } from '../../utils/documentUtils'
import { useNotification } from '../../context/NotificationContext'
import apiClient from '../../services/api'

/**
 * Reusable DocumentItem component
 * Handles document display, icon rendering, loading states, and download functionality
 * 
 * @param {Object} document - Document object with id, name, and type
 * @param {Function} onDownloadStart - Optional callback when download starts
 * @param {Function} onDownloadComplete - Optional callback when download completes
 * @param {string} className - Additional CSS classes
 */
const DocumentItem = ({ 
  document, 
  onDownloadStart, 
  onDownloadComplete, 
  className = '' 
}) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const { showError, showSuccess } = useNotification()
  
  // Get appropriate icon and color for document type
  const IconComponent = getDocumentIcon(document.type)
  const iconColor = getDocumentColor(document.type)

  /**
   * Handle document download with loading state and error handling
   */
  const handleDownload = async () => {
    try {
      // Set loading state
      setIsDownloading(true)
      onDownloadStart?.(document.id)
      
      // Get pre-signed download URL
      const response = await apiClient.getDocumentDownload(document.id)
      
      if (response.url) {
        // Open download URL in new tab
        window.open(response.url, '_blank')
        showSuccess(`Securely opening ${document.name}`, 'Opening Document')
        onDownloadComplete?.(document.id, true)
      } else {
        throw new Error('No download URL received')
      }
    } catch (error) {
      console.error('Download failed:', error)
      showError(`Failed to download ${document.name}. Please try again.`, 'Download Error')
      onDownloadComplete?.(document.id, false)
    } finally {
      // Clear loading state
      setIsDownloading(false)
    }
  }

  return (
    <div 
      className={`flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ${className}`}
      onClick={handleDownload}
    >
      {/* Document icon */}
      <div className={`p-2 bg-gray-100 rounded-lg flex-shrink-0 ${iconColor}`}>
        <IconComponent className="w-5 h-5" />
      </div>
      
      {/* Document info */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 text-sm truncate">
          {document.name}
        </h4>
        <p className="text-xs text-gray-500">
          {document.type || 'Unknown type'}
        </p>
      </div>
      
      {/* Download icon with loading state */}
      <div className="flex-shrink-0">
        {isDownloading ? (
          <div className="w-5 h-5 border border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
        ) : (
          <Download className="w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors" />
        )}
      </div>
    </div>
  )
}

export default DocumentItem
